import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Car, 
  LayoutDashboard,
  FileText,
  AlertTriangle,
  Store,
  Gift,
  BarChart3,
  Monitor,
  ListChecks,
  Megaphone,
  CreditCard,
  TrendingUp,
  Settings,
  LogOut,
  Sparkles,
  Search,
  RefreshCw,
  Loader2,
  CheckCircle,
  XCircle,
  Eye,
  Receipt,
  Plus,
  Trash2,
  Edit2,
  Activity,
  Accessibility,
  Users,
  ParkingSquare,
  Filter,
  Badge,
  Wallet,
  DollarSign,
  Package
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import axios from "axios";
import { toast } from "sonner";

const API = '/api';

const navItems = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/admin/dashboard" },
  { icon: FileText, label: "Registrations", path: "/admin/registrations" },
  { icon: Activity, label: "Live Sessions", path: "/admin/sessions" },
  { icon: ParkingSquare, label: "Space Management", path: "/admin/spaces" },
  { icon: AlertTriangle, label: "OCR Exceptions", path: "/admin/ocr-exceptions" },
  { icon: Store, label: "Retailers", path: "/admin/retailers" },
  { icon: Gift, label: "Rewards", path: "/admin/rewards" },
  { icon: Sparkles, label: "Entitlements", path: "/admin/entitlements-v2" },
  { icon: Receipt, label: "Receipts", path: "/admin/receipts" },
  { icon: BarChart3, label: "Analytics", path: "/admin/analytics" },
  { icon: Monitor, label: "Screens", path: "/admin/screens" },
  { icon: ListChecks, label: "Playlists", path: "/admin/playlists" },
  { icon: Megaphone, label: "Advertisers", path: "/admin/advertisers" },
  { icon: TrendingUp, label: "Campaigns", path: "/admin/campaigns" },
  { icon: CreditCard, label: "Billing", path: "/admin/billing" },
  { icon: TrendingUp, label: "Revenue", path: "/admin/revenue" },
  { icon: Wallet, label: "Commercial", path: "/admin/commercial" },
  { icon: DollarSign, label: "Finance Centre", path: "/admin/commercial/finance" },
  { icon: Package, label: "Products & Tariffs", path: "/admin/commercial/products-tariffs" },
  { icon: Settings, label: "Settings", path: "/admin/settings" },
];

const CATEGORIES = [
  { value: "disabled", label: "Disabled", icon: Accessibility, color: "bg-blue-500" },
  { value: "staff", label: "Staff", icon: Users, color: "bg-purple-500" },
  { value: "reserved", label: "Reserved", icon: Badge, color: "bg-yellow-500" },
  { value: "general", label: "General", icon: Car, color: "bg-green-500" }
];

export default function AdminSpaceManagement() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [assignments, setAssignments] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [formData, setFormData] = useState({
    vehicle_registration: "",
    category: "disabled",
    override_pricing: false,
    free_parking: true,
    badge_number: "",
    notes: "",
    max_hours: ""
  });

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (categoryFilter !== "all") params.append("category", categoryFilter);
      params.append("status", "active");
      params.append("limit", "200");

      const [assignmentsRes, statsRes] = await Promise.all([
        axios.get(`${API}/spaces/list?${params.toString()}`),
        axios.get(`${API}/spaces/stats`)
      ]);

      setAssignments(assignmentsRes.data?.assignments || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Failed to fetch spaces:", error);
      toast.error("Failed to load space assignments");
    } finally {
      setLoading(false);
    }
  }, [categoryFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = () => {
    document.cookie = "admin_session=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    navigate("/admin");
  };

  const handleCreateOrUpdate = async () => {
    if (!formData.vehicle_registration.trim()) {
      toast.error("Vehicle registration is required");
      return;
    }

    try {
      const payload = {
        vehicle_registration: formData.vehicle_registration.toUpperCase().trim(),
        category: formData.category,
        site_id: "site_001",
        override_pricing: formData.override_pricing,
        free_parking: formData.free_parking,
        badge_number: formData.badge_number || null,
        notes: formData.notes || null,
        max_hours: formData.max_hours ? parseInt(formData.max_hours) : null
      };

      if (selectedAssignment) {
        await axios.put(`${API}/spaces/${selectedAssignment.assignment_id}`, payload);
        toast.success("Assignment updated successfully");
      } else {
        await axios.post(`${API}/spaces/assign`, payload);
        toast.success("Space assignment created successfully");
      }

      setShowCreateDialog(false);
      setSelectedAssignment(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Failed to save assignment:", error);
      toast.error(error.response?.data?.detail || "Failed to save assignment");
    }
  };

  const handleDelete = async (assignmentId) => {
    if (!window.confirm("Are you sure you want to remove this assignment?")) return;

    try {
      await axios.delete(`${API}/spaces/${assignmentId}`);
      toast.success("Assignment removed successfully");
      fetchData();
    } catch (error) {
      console.error("Failed to delete assignment:", error);
      toast.error("Failed to remove assignment");
    }
  };

  const handleEdit = (assignment) => {
    setSelectedAssignment(assignment);
    setFormData({
      vehicle_registration: assignment.vehicle_registration,
      category: assignment.category,
      override_pricing: assignment.override_pricing,
      free_parking: assignment.free_parking,
      badge_number: assignment.badge_number || "",
      notes: assignment.notes || "",
      max_hours: assignment.max_hours?.toString() || ""
    });
    setShowCreateDialog(true);
  };

  const resetForm = () => {
    setFormData({
      vehicle_registration: "",
      category: "disabled",
      override_pricing: false,
      free_parking: true,
      badge_number: "",
      notes: "",
      max_hours: ""
    });
  };

  const getCategoryIcon = (category) => {
    const cat = CATEGORIES.find(c => c.value === category);
    return cat ? cat.icon : Car;
  };

  const getCategoryColor = (category) => {
    const cat = CATEGORIES.find(c => c.value === category);
    return cat ? cat.color : "bg-gray-500";
  };

  const getCategoryLabel = (category) => {
    const cat = CATEGORIES.find(c => c.value === category);
    return cat ? cat.label : category;
  };

  const filteredAssignments = assignments.filter(a => {
    const matchesSearch = !searchTerm || 
      a.vehicle_registration?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.badge_number?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  return (
    <div className="min-h-screen bg-slate-950 flex" data-testid="admin-space-management">
      {/* Sidebar */}
      <div className="w-64 bg-slate-900 border-r border-slate-800 flex flex-col">
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #2563EB 0%, #10B981 100%)' }}
            >
              <Car className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-white">ParkWise</h1>
              <p className="text-xs text-slate-500">Admin Console</p>
            </div>
          </div>
        </div>
        
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = item.path === "/admin/spaces";
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-blue-500/20 text-blue-300"
                    : "text-slate-400 hover:bg-slate-800 hover:text-white"
                }`}
              >
                <Icon className="w-5 h-5" />
                {item.label}
              </button>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-800">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-slate-400 hover:bg-slate-800 hover:text-white transition-colors"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 p-8 overflow-y-auto">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                <ParkingSquare className="w-8 h-8 text-blue-400" />
                Designated Space Management
              </h1>
              <p className="text-slate-400 mt-1">
                Manage parking permits and space assignments
              </p>
            </div>
            <Button 
              onClick={() => { resetForm(); setSelectedAssignment(null); setShowCreateDialog(true); }}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="add-assignment-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Assignment
            </Button>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center">
                      <Car className="w-5 h-5 text-slate-300" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">{stats.total_assignments || 0}</p>
                      <p className="text-xs text-slate-400">Total</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {CATEGORIES.map(cat => (
                <Card key={cat.value} className="bg-slate-900 border-slate-800">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg ${cat.color}/20 flex items-center justify-center`}>
                        <cat.icon className={`w-5 h-5 text-${cat.color.replace('bg-', '')}`} />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-white">
                          {stats.by_category?.[cat.value] || 0}
                        </p>
                        <p className="text-xs text-slate-400">{cat.label}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Filters */}
          <Card className="bg-slate-900 border-slate-800 mb-6">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      placeholder="Search by vehicle or badge number..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      data-testid="search-input"
                      className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                    />
                  </div>
                </div>

                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-[160px] bg-slate-800 border-slate-700 text-white" data-testid="category-filter">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="all" className="text-white">All Categories</SelectItem>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value} className="text-white">
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button 
                  onClick={fetchData} 
                  variant="outline" 
                  className="border-slate-700 text-slate-300 hover:bg-slate-800"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Assignments Table */}
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader className="border-b border-slate-800">
              <CardTitle className="text-white flex items-center gap-2">
                <Badge className="w-5 h-5" />
                Space Assignments ({filteredAssignments.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                </div>
              ) : filteredAssignments.length === 0 ? (
                <div className="text-center py-12">
                  <ParkingSquare className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">No assignments found</p>
                  <Button
                    onClick={() => setShowCreateDialog(true)}
                    className="mt-4"
                    variant="outline"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create First Assignment
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-800/50">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Vehicle</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Category</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Badge #</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Pricing</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Free</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Notes</th>
                        <th className="text-right px-4 py-3 text-xs font-medium text-slate-400 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                      {filteredAssignments.map((assignment) => {
                        const CategoryIcon = getCategoryIcon(assignment.category);
                        return (
                          <tr 
                            key={assignment.assignment_id} 
                            className="hover:bg-slate-800/30 transition-colors"
                            data-testid={`assignment-row-${assignment.assignment_id}`}
                          >
                            <td className="px-4 py-3">
                              <span className="font-mono font-bold text-white">
                                {assignment.vehicle_registration}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(assignment.category)}/20 text-white`}>
                                <CategoryIcon className="w-3 h-3" />
                                {getCategoryLabel(assignment.category)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-slate-300 text-sm">
                              {assignment.badge_number || "—"}
                            </td>
                            <td className="px-4 py-3">
                              {assignment.override_pricing ? (
                                <CheckCircle className="w-4 h-4 text-green-400" />
                              ) : (
                                <XCircle className="w-4 h-4 text-slate-500" />
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {assignment.free_parking ? (
                                <span className="text-green-400 text-sm font-medium">Free</span>
                              ) : (
                                <span className="text-slate-500 text-sm">Standard</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-slate-400 text-sm max-w-[200px] truncate">
                              {assignment.notes || "—"}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-slate-400 hover:text-white"
                                  onClick={() => handleEdit(assignment)}
                                  data-testid={`edit-${assignment.assignment_id}`}
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-400 hover:text-red-300"
                                  onClick={() => handleDelete(assignment.assignment_id)}
                                  data-testid={`delete-${assignment.assignment_id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              {selectedAssignment ? (
                <>
                  <Edit2 className="w-5 h-5" />
                  Edit Assignment
                </>
              ) : (
                <>
                  <Plus className="w-5 h-5" />
                  New Space Assignment
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-300">Vehicle Registration *</Label>
              <Input
                value={formData.vehicle_registration}
                onChange={(e) => setFormData({ ...formData, vehicle_registration: e.target.value.toUpperCase() })}
                placeholder="AB12 CDE"
                className="bg-slate-800 border-slate-700 text-white mt-1"
                data-testid="vrm-input"
              />
            </div>

            <div>
              <Label className="text-slate-300">Category *</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value} className="text-white">
                      <div className="flex items-center gap-2">
                        <cat.icon className="w-4 h-4" />
                        {cat.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {formData.category === "disabled" && (
              <div>
                <Label className="text-slate-300">Badge Number</Label>
                <Input
                  value={formData.badge_number}
                  onChange={(e) => setFormData({ ...formData, badge_number: e.target.value })}
                  placeholder="BB123456"
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
            )}

            <div className="flex items-center justify-between">
              <Label className="text-slate-300">Override Pricing</Label>
              <Switch
                checked={formData.override_pricing}
                onCheckedChange={(c) => setFormData({ ...formData, override_pricing: c })}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label className="text-slate-300">Free Parking</Label>
              <Switch
                checked={formData.free_parking}
                onCheckedChange={(c) => setFormData({ ...formData, free_parking: c })}
              />
            </div>

            <div>
              <Label className="text-slate-300">Max Hours (optional)</Label>
              <Input
                type="number"
                value={formData.max_hours}
                onChange={(e) => setFormData({ ...formData, max_hours: e.target.value })}
                placeholder="e.g., 3"
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>

            <div>
              <Label className="text-slate-300">Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes..."
                className="bg-slate-800 border-slate-700 text-white mt-1"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreateDialog(false)}
              className="border-slate-700 text-slate-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateOrUpdate}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="save-assignment-btn"
            >
              {selectedAssignment ? "Update" : "Create"} Assignment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
